import React, { useMemo } from 'react';
import { Eye, Edit, Trash2, Phone, Mail, Globe, MapPin, Users, Building } from 'lucide-react';
import { useEntityTableColumns } from '../../hooks/useEntityConfig';
import { getFieldDisplayValue, getFieldBadge } from '../../utils/entityConfig';

/**
 * Dynamic Entity Table Component
 * Renders a table based on entity configuration
 */
const DynamicEntityTable = ({
  entityName,
  data = [],
  loading = false,
  onView,
  onEdit,
  onDelete,
  onSelect,
  selectedItems = new Set(),
  selectAll = false,
  onSelectAll,
  customRender = {},
  onRowDoubleClick = null,
  showActions = true,
  customColumns = null, // Allow custom columns to override config
  refs = {},
  className = ''
}) => {
  const { columns: configColumns, loading: configLoading, config } = useEntityTableColumns(entityName);
  
  // Use custom columns if provided, otherwise use config columns
  const columns = useMemo(() => {
    const result = customColumns || configColumns || [];
    // Only log in development and only when we have columns or there's an actual error
    if (process.env.NODE_ENV === 'development') {
      if (result.length > 0) {
        console.log(`✅ [DynamicEntityTable] ${entityName}: Using ${result.length} columns`);
      } else if (!configLoading && !loading && !customColumns && configColumns?.length === 0) {
        console.warn(`⚠️ [DynamicEntityTable] ${entityName}: No columns available after loading`);
      }
    }
    return result;
  }, [customColumns, configColumns, entityName, configLoading, loading]);

  // Determine field icon based on field name/type
  const getFieldIcon = (fieldName) => {
    const name = fieldName.toLowerCase();
    if (name.includes('phone')) return <Phone className="h-4 w-4" />;
    if (name.includes('email')) return <Mail className="h-4 w-4" />;
    if (name.includes('website')) return <Globe className="h-4 w-4" />;
    if (name.includes('address') || name.includes('location')) return <MapPin className="h-4 w-4" />;
    if (name.includes('salesperson') || name.includes('user')) return <Users className="h-4 w-4" />;
    if (name.includes('company')) return <Building className="h-4 w-4" />;
    return null;
  };

  /**
   * Render cell value based on field configuration
   */
  const renderCellValue = (item, column) => {
    if (!column || !column.key) {
      console.warn('⚠️ [DynamicEntityTable] Column missing key:', column);
      return '-';
    }

    const field = column.field;
    // Try multiple ways to access the value
    let value = item[column.key];
    
    // If value is undefined, try accessing with different casing
    if (value === undefined || value === null) {
      const keyLower = column.key.toLowerCase();
      const matchingKey = Object.keys(item).find(k => k.toLowerCase() === keyLower);
      if (matchingKey) {
        value = item[matchingKey];
      }
    }

    // Check for custom renderer first
    if (customRender[column.key]) {
      const rendered = customRender[column.key](item, value);
      if (rendered !== undefined && rendered !== null) {
        return rendered;
      }
    }

    // For lookup fields (e.g., SalespersonID), prefer showing the name field if available
    if (field && field.lookup && field.lookup.enabled) {
      const fieldName = column.key || field.name || '';
      // Check for name field (e.g., SalespersonName for SalespersonID)
      const nameField = fieldName.replace(/ID$/, 'Name') || fieldName.replace(/Id$/, 'Name');
      if (item[nameField] && item[nameField] !== null && item[nameField] !== '') {
        // Use the name field value instead
        const nameValue = item[nameField];
        
        // Handle badge fields
        const badge = getFieldBadge(field, nameValue);
        if (badge) {
          return (
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getBadgeColorClass(badge.color)}`}>
              {badge.label || nameValue}
            </span>
          );
        }
        
        return nameValue;
      }
    }

    // Handle badge fields
    const badge = getFieldBadge(field, value);
    if (badge) {
      return (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getBadgeColorClass(badge.color)}`}>
          {badge.label || value}
        </span>
      );
    }

    // Format value based on field type
    const displayValue = getFieldDisplayValue(field, value, item);

    // Handle boolean values with icons
    if (field && field.dataType === 'boolean') {
      return displayValue === 'Yes' ? '✓' : '✗';
    }

    // If displayValue is null, undefined, or empty, return a fallback
    if (displayValue === null || displayValue === undefined || displayValue === '') {
      return '-';
    }

    return displayValue;
  };

  /**
   * Get Tailwind color classes for badges
   */
  const getBadgeColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
      green: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      yellow: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
      red: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
      gray: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
      purple: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
      orange: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
    };
    return colorMap[color] || colorMap.gray;
  };

  if (configLoading || loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-8 text-center">
          <p className="text-gray-600 dark:text-gray-300">No data available</p>
        </div>
      </div>
    );
  }

  // Check if we have any visible columns
  const visibleColumns = columns ? columns.filter(col => col.visible !== false) : [];
  
  if (visibleColumns.length === 0 && !configLoading && !loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-8 text-center">
          <p className="text-yellow-600 dark:text-yellow-400 font-medium mb-2">⚠️ No columns configured</p>
          <p className="text-gray-600 dark:text-gray-300 text-sm">
            Please configure list view fields for the <strong>{entityName}</strong> entity in the entity configuration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              {/* Selection checkbox column */}
              {onSelect && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={onSelectAll}
                      className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                    />
                  </div>
                </th>
              )}
              
              {/* Dynamic columns from config */}
              {visibleColumns.map((column) => {
                const icon = getFieldIcon(column.key);
                return (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    <div className="flex items-center space-x-1">
                      {icon}
                      <span>{column.label}</span>
                    </div>
                  </th>
                );
              })}
              
              {/* Actions column */}
              {showActions && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {(() => {
              const renderStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
              const rows = data.map((item, rowIndex) => {
              // Determine item ID with robust fallback for different entity types
              const primaryKey = config?.entity?.primaryKey;
              let itemId = primaryKey ? item[primaryKey] : undefined;
              
              // Fallback: check for common ID patterns if primaryKey didn't work
              if (itemId === undefined || itemId === null) {
                // Try common ID field patterns based on entity name
                const entityIdField = entityName ? `${entityName.charAt(0).toUpperCase() + entityName.slice(1)}ID` : null;
                itemId = item[entityIdField] || item.id || item.ID || item[Object.keys(item).find(k => k.toLowerCase().endsWith('id'))] || rowIndex;
              }
              
              // Ensure itemId is a number for consistent comparison
              const numericItemId = typeof itemId === 'number' ? itemId : 
                                   (typeof itemId === 'string' && !isNaN(parseInt(itemId, 10))) ? parseInt(itemId, 10) : itemId;
              
              const isSelected = selectedItems.has(numericItemId);
              
              return (
                <tr
                  key={numericItemId || rowIndex}
                  ref={el => refs[numericItemId] = el}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${onRowDoubleClick ? 'cursor-pointer' : ''} ${
                    isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                  onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(item) : undefined}
                  title={onRowDoubleClick ? 'Double-click to view details' : undefined}
                >
                  {/* Selection checkbox */}
                  {onSelect && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onSelect(numericItemId)}
                        className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                      />
                    </td>
                  )}
                  
                  {/* Dynamic cells */}
                  {visibleColumns.map((column) => {
                    const cellValue = renderCellValue(item, column);
                    return (
                      <td
                        key={column.key}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                      >
                        {cellValue}
                      </td>
                    );
                  })}
                  
                  {/* Actions */}
                  {showActions && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {onView && (
                          <button
                            onClick={() => onView(item)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => onEdit(item)}
                            className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(numericItemId)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
              });

              const renderDuration = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - renderStart;
              if (renderDuration > 25) {
                console.debug(`[DynamicEntityTable] ${entityName} rendered ${data.length} rows in ${Math.round(renderDuration)}ms`);
              }

              return rows;
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DynamicEntityTable;

